import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { formatDate } from '../../helpers/UtilsFunctions';

const AssignedProjectsTable = ({ assignedProjects }) => {
  const { mode } = useThemeMode();
  const { hasReadPermission } = usePermissions();
  const navigate = useNavigate();

  // Process assigned projects data from API
  const projects = assignedProjects || [];

  // Pagination: show 5 rows per page
  const pageSize = 5;
  const [page, setPage] = useState(0);
  const totalPages = useMemo(() => Math.ceil((projects?.length || 0) / pageSize), [projects?.length]);
  const pagedProjects = useMemo(() => {
    const start = page * pageSize;
    return projects.slice(start, start + pageSize);
  }, [projects, page]);

  // Handle project name click to navigate to kanban view
  const handleProjectClick = (projectId) => {
    // Only navigate if user has read permission for tasks
    if (!hasReadPermission('task')) {
      return;
    }
    
    navigate(`/project/${projectId}/tasks`);
  };

  // Get status color based on project status
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return '#4CAF50';
      case 'planned':
        return '#2196F3';
      case 'completed':
        return '#26c3c0';
      case 'on-hold':
        return '#FF9800';
      default:
        return '#666666';
    }
  };

  return (
    <div style={{
      backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
      borderRadius: '12px',
      padding: '24px',
      border: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '20px',
        flexShrink: 0
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          margin: 0,
          color: mode === 'dark' ? '#ffffff' : '#333333',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          Assigned Projects
        </h3>
      </div>

      {/* Table Container with Scroll */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '16px',
          padding: '12px 0',
          borderBottom: mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0',
          marginBottom: '8px',
          flexShrink: 0
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: mode === 'dark' ? '#888888' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif',
            textTransform: 'uppercase'
          }}>
            Project Name
          </div>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: mode === 'dark' ? '#888888' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif',
            textTransform: 'uppercase'
          }}>
            Due Date
          </div>
        </div>

        {/* Project Rows */}
        <div>
          {pagedProjects.length > 0 ? pagedProjects.map((project, idx) => {
            const index = page * pageSize + idx;
            const dueDate = formatDate(project.end_date);
            
            return (
              <div
                key={project.id || index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  gap: '10px',
                  padding: '8px 0',
                  minHeight: '40px',
                  borderBottom: index < projects.length - 1 ? (mode === 'dark' ? '1px solid #333' : '1px solid #e0e0e0') : 'none',
                  alignItems: 'center',
                  cursor: hasReadPermission('task') ? 'pointer' : 'default'
                }}
                onClick={() => handleProjectClick(project.id)}
                title="Click to view project tasks"
              >
                {/* Project Name */}
                <div 
                  style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: mode === 'dark' ? '#ffffff' : '#333333',
                    fontFamily: 'Urbanist, Arial, sans-serif'
                  }}
                >
                  {project.project_name}
                </div>

                {/* Due Date */}
                <div style={{
                  fontSize: '14px',
                  color: mode === 'dark' ? '#cccccc' : '#666666',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {dueDate}
                </div>
              </div>
            );
          }) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100px',
              color: mode === 'dark' ? '#888888' : '#666666',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              No assigned projects found
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '8px',
            marginTop: '12px'
          }}>
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                background: 'transparent',
                color: mode === 'dark' ? '#fff' : '#333',
                cursor: page === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Prev
            </button>
            <span style={{ fontSize: '12px', color: mode === 'dark' ? '#aaa' : '#666' }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              style={{
                padding: '6px 10px',
                borderRadius: '6px',
                border: `1px solid ${mode === 'dark' ? '#444' : '#ddd'}`,
                background: 'transparent',
                color: mode === 'dark' ? '#fff' : '#333',
                cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignedProjectsTable;
